import { useState, useEffect } from "react";
import { TextField, InputLabel, IconButton, InputAdornment, Button } from "@mui/material";
import { RiMailLine, RiEyeLine, RiEyeOffLine } from "@remixicon/react";
import { dotToComma } from "../Helper/generalhelper";
import { FiExternalLink } from "react-icons/fi";
import { Lock } from "@mui/icons-material";

const TextFieldComponent = ({
  id,
  name,
  type,
  value,
  onChange,
  error,
  label,
  isNotMandatory,
  autogenerate = false,
  disabled = false,
  disableEye = false,
  onlyNumber = false,
  showLock = false,
  onLockClick,
  placeHolder = "",
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [internalValue, setInternalValue] = useState(value);

  // Update internal value when prop changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Generates a random password of `length` characters from the given character set
  const generateRandomPassword = (length = 10) => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Toggles the visibility of the password
  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  // Generates a random password and updates the field
  const handleAutoGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    onChange({
      target: {
        name,
        value: newPassword,
      },
    });
  };

  const handleFocus = (e) => {
    if (type === "date" && !value) {
      const today = new Date().toISOString().split("T")[0];
      onChange({ target: { name, value: today } });
    } else if (type === "time" && !value) {
      const now = new Date();
      const timeString = now.toTimeString().slice(0, 5); // Format: "HH:MM"
      onChange({ target: { name, value: timeString } });
    }
  };

  const handleNumberChange = (e) => {
    const inputValue = e.target.value;

    if (onlyNumber) {
      // Allow empty string, numbers, dots, and commas
      if (inputValue === '' || /^[0-9.,]*$/.test(inputValue)) {
        setInternalValue(inputValue);
        // Pass the raw value (without formatting) to parent
        onChange({
          target: {
            name: e.target.name,
            value: inputValue
          }
        });
      }
    } else {
      onChange(e);
    }
  };

  const handleNumberBlur = (e) => {
    if (onlyNumber) {
      const inputValue = e.target.value;

      // If empty, do nothing
      if (inputValue === '') {
        onChange({ target: { name: e.target.name, value: '' } });
        return;
      }

      const formattedValue = dotToComma(inputValue);

      setInternalValue(formattedValue);
      onChange({
        target: {
          name: e.target.name,
          value: formattedValue
        }
      });
    }
  };

  const handleKeyDown = (e) => {
    if (onlyNumber) {
      // Allow: backspace, delete, tab, escape, enter, arrows
      if ([
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
      ].includes(e.key)) {
        return;
      }

      // Allow: numbers (0-9), decimal point (.), and comma (,)
      if ((e.key >= '0' && e.key <= '9') || e.key === '.' || e.key === ',') {
        return; // Allow all dots and commas during typing
      }

      // Prevent all other keys
      e.preventDefault();
    }
  };

  return (
    <>
      <InputLabel shrink htmlFor={id} error={!!error}>
        {label}
        <span className="text-error">{isNotMandatory ? "" : "*"}</span>
        {showLock && (
          <IconButton size="small" onClick={onLockClick}>
            <Lock fontSize="small" />
          </IconButton>
        )}
      </InputLabel>

      <TextField
        id={id}
        name={name}
        placeholder={placeHolder}
        value={onlyNumber ? internalValue : value}
        error={!!error}
        onChange={onlyNumber ? handleNumberChange : onChange}
        onBlur={onlyNumber ? handleNumberBlur : undefined}
        onKeyDown={onlyNumber ? handleKeyDown : undefined}
        disabled={disabled}
        onFocus={handleFocus}
        helperText={error}
        type={type === "password" ? (showPassword ? "text" : "password") : type || "text"}
        fullWidth
        sx={{
          "& .MuiInputBase-root": {
            backgroundColor: disabled ? "#f0f0f0" : "white",
            borderRadius: "4px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled ? "#d6d6d6" : undefined,
          },
          ...((type === "date" || type === "time") && {
            "& input::-webkit-calendar-picker-indicator": {
              cursor: "pointer",
            },
          }),
        }}
        {...props}
        InputProps={{
          inputProps: onlyNumber
            ? {
              inputMode: "decimal",
            }
            : undefined,
          // endAdornment: type === "password" && (
          //   <InputAdornment position="end">
          //     <IconButton onClick={handleTogglePassword} sx={{ border: "none", boxShadow: "none", backgroundColor: "#eef2f7", borderRadius: "0px", borderTopLeftRadius: "0px", borderBottomLeftRadius: "0px" }}>
          //       {showPassword ? <RiEyeOffLine fontSize="small" /> : <RiEyeLine fontSize="small" />}
          //     </IconButton>
          //     {
          //       autogenerate &&
          //       <Button
          //         sx={{ cursor: "pointer", borderLeft: "1px solid #ced4da!important", backgroundColor: "#eef2f7", color: "#4c4c5c", borderTopLeftRadius: "0px", borderBottomLeftRadius: "0px", border: "none" }}
          //         onClick={handleAutoGeneratePassword}
          //       >
          //         Auto generate
          //       </Button>
          //     }
          //   </InputAdornment>
          // ),
          endAdornment: (
            <InputAdornment position="end">
              {/* 🔐 Password-type adornments */}
              {type === "password" && (
                <>
                  <IconButton
                    disabled={disableEye}
                    onClick={handleTogglePassword}
                    sx={{
                      border: "none",
                      boxShadow: "none",
                      backgroundColor: "#eef2f7",
                      borderRadius: 0,
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    {showPassword ? (
                      <RiEyeOffLine fontSize="small" />
                    ) : (
                      <RiEyeLine fontSize="small" />
                    )}
                  </IconButton>

                  {autogenerate && (
                    <Button
                      disabled={disableEye}
                      onClick={handleAutoGeneratePassword}
                      sx={{
                        cursor: "pointer",
                        borderLeft: "1px solid #ced4da!important",
                        backgroundColor: "#eef2f7",
                        color: "#4c4c5c",
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        border: "none",
                      }}
                    >
                      Auto generate
                    </Button>
                  )}
                </>
              )}

              {name === "vWebsite" && (
                <IconButton
                  onClick={() => {
                    if (value) {
                      const url =
                        value.startsWith("http://") || value.startsWith("https://")
                          ? value
                          : `https://${value}`;
                      window.open(url, "_blank");
                    }
                  }}
                  sx={{
                    backgroundColor: "#eef2f7",
                    borderRadius: 0,
                    ml: 1,
                    "&:hover": {
                      backgroundColor: "#e0e0e0",
                    },
                  }}
                >
                  <FiExternalLink color="#6f42c1" size={18} />
                </IconButton>
              )}
            </InputAdornment>
          ),
          startAdornment: type === "email" && (
            <InputAdornment position="start" sx={{ marginRight: "0px" }}>
              <IconButton sx={{ border: "none", boxShadow: "none", backgroundColor: "#eef2f7", borderRadius: "0px", borderTopLeftRadius: "4px", borderBottomLeftRadius: "4px" }}><RiMailLine /></IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};

export default TextFieldComponent;